import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["section", "navItem"]
  static values = {
    offset: { type: Number, default: 150 }
  }

  connect() {
    console.log('ScrollSpy controller connected')
    this.currentActiveIndex = null
    this.debounceTimer = null
    this.isScrolling = false
    this.navigationWrap = document.querySelector('.navigation_wrap')

    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize())
    } else {
      this.initialize()
    }
  }

  initialize() {
    // Use scroll event with debounce instead of IntersectionObserver
    // This prevents jumping on dynamic content like TinyMCE
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true })

    // Set initial active state
    this.updateActiveState()
    this.updateNavigationPosition()
  }

  disconnect() {
    window.removeEventListener('scroll', this.handleScroll.bind(this))
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  handleScroll() {
    // Update navigation position immediately (no debounce for smooth animation)
    this.updateNavigationPosition()

    // Don't update during programmatic scrolling (when clicking nav items)
    if (this.isScrolling) return

    // Debounce to prevent too frequent updates
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      this.updateActiveState()
    }, 100) // 100ms debounce
  }

  updateNavigationPosition() {
    if (!this.navigationWrap) return

    // Add 'scrolled' class when user scrolls down past 100px
    if (window.scrollY > 100) {
      this.navigationWrap.classList.add('scrolled')
    } else {
      this.navigationWrap.classList.remove('scrolled')
    }
  }

  updateActiveState() {
    // Find which section is currently in view
    // A section is "active" when its top is within the offset zone
    const scrollPosition = window.scrollY + this.offsetValue

    let activeIndex = null
    let closestDistance = Infinity

    // Find the section whose top is closest to our scroll position
    this.sectionTargets.forEach(section => {
      const sectionTop = section.offsetTop
      const distance = Math.abs(scrollPosition - sectionTop)

      // Only consider sections that are at or past the scroll position
      if (scrollPosition >= sectionTop - this.offsetValue && distance < closestDistance) {
        closestDistance = distance
        activeIndex = section.dataset.scrollIndex
      }
    })

    if (activeIndex !== null && activeIndex !== this.currentActiveIndex) {
      this.setActiveNavItem(activeIndex)
    }
  }

  setActiveNavItem(index) {
    if (this.currentActiveIndex === index) return

    this.currentActiveIndex = index
    console.log('Setting active nav item:', index)

    // Remove active class from all nav items
    this.navItemTargets.forEach(item => {
      item.classList.remove('active')
    })

    // Add active class to matching nav item
    const activeNavItem = this.navItemTargets.find(
      item => item.dataset.scrollNav === index
    )

    if (activeNavItem) {
      activeNavItem.classList.add('active')
    }
  }

  // Handle click on nav item for smooth scrolling
  scrollToSection(event) {
    event.preventDefault()

    const navItem = event.currentTarget
    const targetIndex = navItem.dataset.scrollNav
    const targetSection = this.sectionTargets.find(
      section => section.dataset.scrollIndex === targetIndex
    )

    if (targetSection) {
      // Set flag to prevent scroll event from triggering during programmatic scroll
      this.isScrolling = true

      // Immediately update active state for instant feedback
      this.setActiveNavItem(targetIndex)

      // Calculate target position (align section top with navigation offset)
      const targetPosition = targetSection.offsetTop - this.offsetValue + 50

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      })

      // Reset flag after smooth scroll completes
      setTimeout(() => {
        this.isScrolling = false
      }, 1000) // Smooth scroll typically takes ~500-800ms
    }
  }
}
